import { type ChatInterface } from "@mlc-ai/web-llm";
import { getEmbeddings } from "../../../candle-wasm-bert/utils.js";
const bertWorker = new Worker(new URL('../../../candle-wasm-bert/bertWorker.js', import.meta.url), {
  type: "module",
});

import { createActor } from "../declarations/instance/index.js";
import { ActorSubclass } from "@dfinity/agent";
import { _SERVICE } from "../declarations/instance/instance.did.js";
const canister: ActorSubclass<_SERVICE> = createActor("6n4um-kiaaa-aaaak-afdlq-cai", { agentOptions: { host: 'https://ic0.app' } });


export default class LocalChatUI {
  private chat: ChatInterface;
  private chatLoaded = false;
  public requestInProgress = false;
  // We use a request chain to ensure that
  // all requests send to chat are sequentialized
  private chatRequestChain: Promise<void> = Promise.resolve();

  constructor(chat: ChatInterface) {
    this.chat = chat;
  }
  /**
   * Push a task to the execution queue.
   *
   * @param task The task to be executed;
   */
  private pushTask(task: () => Promise<void>) {
    const lastEvent = this.chatRequestChain;
    this.chatRequestChain = lastEvent.then(task);
  }
  // Event handlers
  // all event handler pushes the tasks to a queue
  // that get executed sequentially
  // the tasks previous tasks, which causes them to early stop
  // can be interrupted by chat.interruptGenerate
  async onGenerate(prompt: string, messageUpdate: (kind: string, text: string, append: boolean) => void, setRuntimeStats: (runtimeStats: string) => void) {
    if (this.requestInProgress) {
      return;
    }
    this.pushTask(async () => {
      await this.asyncGenerate(prompt, messageUpdate, setRuntimeStats);
    });
    return this.chatRequestChain;
  }

  async onReset(clearMessages: () => void) {
    if (this.requestInProgress) {
      // interrupt previous generation if any
      this.chat.interruptGenerate();
    }
    // try reset after previous requests finishes
    this.pushTask(async () => {
      await this.chat.resetChat();
      clearMessages();
    });
    return this.chatRequestChain;
  }

  async asyncInitChat(prompt: string, messageUpdate: (kind: string, text: string, append: boolean) => void) {
    if (this.chatLoaded) return;
    this.requestInProgress = true;
    messageUpdate("init", "", true);
    const initProgressCallback = (report: { text: string; }) => {
      messageUpdate("init", report.text, false);
    };
    this.chat.setInitProgressCallback(initProgressCallback);

    try {
      const query = "query: " + prompt; // add search prefix
      const model_base_url = "https://huggingface.co/intfloat/e5-small-v2/resolve/main/";
      const bert_res = await getEmbeddings(
        bertWorker,
        model_base_url + "model.safetensors",
        model_base_url + "tokenizer.json",
        model_base_url + "config.json",
        "intfloat_e5_small_v2", // model id
        [query]
      );
      const embedding = bert_res.output[0];
      const summary_len: number = 300;
      // let summary = await canister.search_query(prompt, embedding, summary_len);
      let response = await canister.search_query_v2(prompt, embedding, summary_len);
      let url = response[0][0][1]
      let overview = response[0][0][1];
      let summary = response[1];

      const config = {
        "conv_config": {
          "system":
`You are project leader and now going to understand some of the developer documentation.
This is a context of this conversation provided from official developer documents. Just read and ready for question.
------
${url}

${overview.split('\n').slice(0, 2)}

${summary}
---------------------
`
        },
        // "context_window_size": 4096,
        // "mean_gen_len": 512,
        "max_gen_len": 1024,
        "temperature": 0.1
      };
      await this.chat.reload("Llama-2-7b-chat-hf-q4f32_1", config, {
        "model_list": [
          {
            "model_url": "https://huggingface.co/mlc-ai/mlc-chat-Llama-2-7b-chat-hf-q4f32_1/resolve/main/",
            "local_id": "Llama-2-7b-chat-hf-q4f32_1"
          },
          {
            "model_url": "https://huggingface.co/mlc-ai/mlc-chat-RedPajama-INCITE-Chat-3B-v1-q4f32_1/resolve/main/",
            "local_id": "RedPajama-INCITE-Chat-3B-v1-q4f32_1"
          },
        ],
        "model_lib_map": {
          "Llama-2-7b-chat-hf-q4f32_1": "https://raw.githubusercontent.com/mlc-ai/binary-mlc-llm-libs/main/Llama-2-7b-chat-hf-q4f32_1-webgpu.wasm",
          "RedPajama-INCITE-Chat-3B-v1-q4f32_1": "https://raw.githubusercontent.com/mlc-ai/binary-mlc-llm-libs/main/RedPajama-INCITE-Chat-3B-v1-q4f32_1-webgpu.wasm",
        },
      });

    } catch (err: unknown) {
      messageUpdate("error", "Init error, " + (err?.toString() ?? ""), true);
      console.log(err);
      await this.unloadChat();
      this.requestInProgress = false;
      return;
    }
    this.requestInProgress = false;
    this.chatLoaded = true;
  }

  private async unloadChat() {
    await this.chat.unload();
    this.chatLoaded = false;
  }

  /**
   * Run generate
   */
  private async asyncGenerate(prompt: string, messageUpdate: (kind: string, text: string, append: boolean) => void, setRuntimeStats: (runtimeStats: string) => void) {
    await this.asyncInitChat(prompt, messageUpdate);
    this.requestInProgress = true;

    if (prompt == "") {
      this.requestInProgress = false;
      return;
    }


    messageUpdate("user", prompt, true);

    messageUpdate("llm", "", true);

    const callbackUpdateResponse = (step: number, msg: string) => {
      messageUpdate("llm", msg, false);
    };

    try {
      let query = 
`Question: "${prompt}"
Answer the question using above briefly.`
      const output = await this.chat.generate(query, callbackUpdateResponse);
      messageUpdate("llm", output, false);
      this.chat.runtimeStatsText().then(stats => setRuntimeStats(stats)).catch(error => console.log(error));
    } catch (err: unknown) {
      messageUpdate("error", "Generate error, " + (err?.toString() ?? ""), true);
      console.log(err);
      await this.unloadChat();
    }
    this.requestInProgress = false;
  }
}